import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import VendorCTAsGroup from '../VendorCTAsGroup/VendorCTAsGroup';
import { selectVendorCTAs } from '../../redux/vendorCTAsReducer';
import { VEHICLE_FALLBACK_IMAGE as vehicleFallbackImage } from '../../constants';
import { FilteredVehicle, GalleryImagesType, Rebate } from '../../types';
import Modal from '../Modal';
import Location from './Location';
import { CpoIcon, Legal, TextContainer } from './VehicleCard.styles';
import {
  VehicleName,
  VehicleInfo,
  CardContentPadding,
  VehicleTrim,
  Milage,
  CpoBLOCK,
  T3StatusContainer,
  CardContainer,
  ImageContainer,
  SingleVehicleImage,
  VdpCtaContainer,
  VdpCTA,
  DisclaimerTitleWrapper,
} from './T3CardStyles';
import CPOLogo from '../../assets/images/CertifiedBadges/CPO';
import ImagesGallery from '../ImagesGallery/ImagesGallery';
import Status from './Status';
import PriceBreakdown from './PriceBreakdown/PriceBreakdown';
import { COLORS } from '../../styles/variables';
import { HeadlineOrderTwo } from '../Common/Common.style';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { CarType, NonAudiDealershipLocation } from '../../types/oneGraphTypes';

interface VehicleCardProps {
  // For Ali: don't forget to set correct types :)
  vehicle: CarType;
  marketType: string;
  vehicleDetailsUrl: string;
  isImageGalleryOn: boolean;
  mainDealer?: string;
}

const T3VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  marketType,
  isImageGalleryOn,
  vehicleDetailsUrl,
  mainDealer,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';
  const { stockCar, geoDistance } = vehicle;
  const {
    id,
    cartypeText,
    salesInfo,
    dealer,
    carPrices,
    images,
    titleText,
    mileage,
    model,
    vin,
    subtitleText,
    dynamicAttributes,
    qualityLabel,
  } = stockCar;
  const { orderStatusText, availableFromDateInfo } = salesInfo;
  const name = model?.name;

  const { vendorCtaGroup } = useSelector(selectVendorCTAs);
  const { listOfDealers, isTier3page } = useSelector(selectAppConfig);

  const isMultipleDealers = listOfDealers.length > 1;

  const isMarketCA = marketType === 'CAN';
  const isNewVehicle = cartypeText === 'N';
  const isAvailableNow = availableFromDateInfo?.value === 'Immediately';
  const vehicleType = isNewVehicle ? 'new' : 'used';

  const isCPO = !!(!isNewVehicle && qualityLabel.length && qualityLabel[0].label === 'CPO');
  const vehicleDataType = cartypeText === 'U' && isCPO ? 'cpo' : vehicleType;

  const isReservedVehicle = salesInfo.saleOrderTypeText?.includes('SOLD');
  const modelImageFromScs = images[0]?.url;
  const vehicleDealerImages = images?.filter((image) => image?.id?.group === 'dealerImages');
  const modelMileage = mileage.value.formatted;

  const isQ5Hybrid = name?.includes('Q5') && name?.includes('TFSI e');
  const modelName = isQ5Hybrid ? `${titleText} Plug-in Hybrid` : titleText;

  // OADD (Tier 3)Stock Number
  const stockNumber = useMemo(() => {
    const stockNumberObject = dynamicAttributes?.find((key) => key?.id === 'VEHICLE_ID');
    if (stockNumberObject) {
      return stockNumberObject?.value;
    }
    return null;
  }, [dynamicAttributes]);

  const nonAudiDealershipLocation = useMemo(() => {
    const defaultResult = {
      multiLocation: false,
      dealerName: '',
      dealerId: '',
    } as NonAudiDealershipLocation;
    // Find NAD Object in dynamic attributes
    const nadObject = dynamicAttributes?.find((key) => key?.id === 'NAD');
    if (!nadObject) return defaultResult;
    // Safely parse the value and handle invalid JSON
    const parsedNadObject = (() => {
      try {
        return JSON.parse(nadObject?.value);
      } catch {
        return null;
      }
    })();
    if (!parsedNadObject) return defaultResult;

    return {
      multiLocation: parsedNadObject?.Multi_location === 'Yes',
      dealerName: parsedNadObject?.Dealer_Name || '',
      dealerId: parsedNadObject?.Audi_Parent_ID || '',
    } as NonAudiDealershipLocation;
  }, [dynamicAttributes]);

  const rebate = useMemo(() => {
    const rebates: Rebate[] = [];
    if (!dynamicAttributes) return [];

    dynamicAttributes?.forEach((attribute) => {
      if (attribute?.id?.startsWith('REBATE')) {
        try {
          const rebateItem = JSON.parse(attribute?.value);
          rebates.push({
            Value: rebateItem?.Amount,
            Name: rebateItem?.OfferName,
            offerDescription: rebateItem?.OfferDescription,
            offerStartDate: rebateItem?.OfferStartDate,
            offerEndDate: rebateItem?.OfferEndDate,
          });
        } catch (error) {
          console.error(`Failed to parse rebate data for key ${attribute?.id}:`, error);
        }
      }
    });
    return rebates;
  }, [dynamicAttributes]);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    text: string | JSX.Element;
  }>({
    isOpen: false,
    title: '',
    text: '',
  });

  const priceLegalText = useMemo(() => {
    if (marketType === 'CAN' && isNewVehicle && !isAvailableNow)
      return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'CAN' && isNewVehicle && isAvailableNow)
      return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'CAN' && !isNewVehicle)
      return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'USA' && isNewVehicle)
      return t('vlp.vehicleResults.vehicleCard.msrpLegalTextUSA');

    // marketType === 'USA' && vehicleType === 'used'
    return (
      <Trans
        i18nKey="vlp.vehicleResults.vehicleCard.dealerPriceLegalText"
        // the global reset css imported in several feature apps
        // contains globally applied oppinionated styling
        components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
      />
    );
  }, [marketType, isNewVehicle, isAvailableNow, t]);

  const onModalOpen = useCallback(
    (e: any, title?: string, text?: string | JSX.Element) => {
      e.preventDefault();
      e.stopPropagation();
      setModalState({
        isOpen: true,
        title: title || '',
        text: text || priceLegalText || '',
      });
    },
    [priceLegalText],
  );

  const onModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      title: '',
      text: '',
    });
  }, []);

  // FIXME: Delete this function when backend will be fixed
  const deleteHPfromTrim = (trim: string) => {
    if (!trim) return null;
    if (trim.includes('!!')) return null;
    if (!trim.includes('HP') && !trim.includes('kW')) return trim;

    const trimNameArray = trim.split(' ');

    const indexOfHP = trimNameArray.includes('HP')
      ? trimNameArray.indexOf('HP')
      : trimNameArray.indexOf('kW');

    const isNumberElementBeforeHP = /^\d+$/.test(trimNameArray[indexOfHP - 1]?.replace(',', ''));

    if (isNumberElementBeforeHP) {
      trimNameArray.splice(indexOfHP - 1, 2);
      return trimNameArray.join(' ');
    }
    trimNameArray.splice(indexOfHP, 1);
    return trimNameArray.join(' ');
  };

  const popoverDescription = useMemo(() => {
    if (isReservedVehicle) return t('vlp.vehicleResults.vehicleCard.reserved');
    if (nonAudiDealershipLocation && nonAudiDealershipLocation?.multiLocation)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.nonAudiLocationPopUpInfo');
    if (isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowCAN.popoverDescription');
    if (isAvailableNow && !isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowUSA.popoverDescription');
    if (!isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionCAN');
    return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionUSA');
  }, [isAvailableNow, isMarketCA, isReservedVehicle, nonAudiDealershipLocation, t]);

  const altFunction = useCallback(() => {
    if (modelImageFromScs && modelImageFromScs.includes('default-images')) {
      return t('vlp.vehicleResults.vehicleCard.vehicleFallbackAlt', { titleText });
    }
    return t('vlp.vehicleResults.vehicleCard.vehicleStandardAlt', { titleText });
  }, [modelImageFromScs, titleText, t]);

  const statusForDot = useMemo(() => {
    if (isReservedVehicle) return 'REVERVED';
    if (isAvailableNow) return 'AVAILABLE';
    return 'PIPELINE';
  }, [isAvailableNow, isReservedVehicle]);

  const vehicleStatus = useMemo(() => {
    if (isReservedVehicle) return 'Reserved';
    if (nonAudiDealershipLocation && nonAudiDealershipLocation?.multiLocation)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.nonAudiLocation');
    if (isAvailableNow)
      return t(`vlp.vehicleResults.vehicleCard.availableNow${marketType}.availabilityLabel`);
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText === 'Intransit From Factory')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.factory');
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText === 'Port Stock')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.port');
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText === 'Intransit Inland')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.inland');
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText == null)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.contactDealer');

    return '';
  }, [
    isReservedVehicle,
    nonAudiDealershipLocation,
    t,
    isAvailableNow,
    marketType,
    vehicleType,
    orderStatusText,
  ]);

  const hasDealerImages = !!vehicleDealerImages?.length;

  // FIX LATER
  // const galleryImagesVar = useMemo(() => {
  //   // if there are at least one image in galleryImages array return that
  //   // otherwise return modelImageFromScs or vehicleFallbackImage
  //   if (vehicleDealerImages && isNewVehicle)
  //     return (
  //       vehicleDealerImages
  //         // For Ali: don't forget to set correct types :)
  //         .map((image: any, index: any) => ({ url: image, type: index.toString() }))
  //         .slice(0, 5)
  //     );
  //   if (galleryImages?.length > 0) return galleryImages;
  //   const modelImage = modelImageFromScs || vehicleFallbackImage;
  //   const singleImageObject: GalleryImagesType = [{ type: null, url: modelImage }];
  //   return singleImageObject;
  // }, [galleryImages, isNewVehicle, modelImageFromScs, vehicleDealerImages]);

  const galleryImagesVar = useMemo(() => {
    // if there are at least one image in galleryImages array return that
    // otherwise return modelImageFromScs or vehicleFallbackImage
    if (images?.length > 0) return images;
    const modelImage = modelImageFromScs || vehicleFallbackImage;
    const singleImageObject = [
      {
        id: { group: 'renderImagesPNG', image: 'vtp4x3n3c' },
        type: 'render',
        url: modelImage,
      },
    ];
    return singleImageObject;
  }, [images, modelImageFromScs]);

  const modelImage = useMemo(() => {
    if (isTier3page && isNewVehicle && vehicleDealerImages?.length)
      return vehicleDealerImages[0]?.url;
    if (modelImageFromScs) return modelImageFromScs;
    return vehicleFallbackImage;
  }, [isNewVehicle, isTier3page, modelImageFromScs, vehicleDealerImages]);

  const unitOfMeasurement = marketType === 'CAN' ? 'km' : 'miles';

  const renderCpoBlock = useMemo(() => {
    return (
      <CpoBLOCK isCPO={isCPO}>
        {isCPO && (
          <CpoIcon>
            <CPOLogo />
          </CpoIcon>
        )}
      </CpoBLOCK>
    );
  }, [isCPO]);

  // FIX Later
  const renderGallery = useMemo(() => {
    return (
      <ImageContainer noPadding>
        {/* {isImageGalleryOn ? (
           <ImagesGallery
             count={galleryImagesVar?.length}
             isNewVehicle={isNewVehicle}
             isReservedVehicle={isReservedVehicle}
             images={galleryImagesVar}
             bodyType={bodyType}
             alt={altFunction()}
             data-cy="vehicle-card-image-gallery"
             isTier3Page
             hasDealerImages={hasDealerImages}
           />
         ) : ( */}
        <a href={vehicleDetailsUrl}>
          <SingleVehicleImage
            src={modelImage}
            alt={altFunction()}
            isNewVehicle={isNewVehicle}
            isReservedVehicle={isReservedVehicle}
            data-cy="vehicle-card-simgle-image"
          />
        </a>
        {/* )} */}
      </ImageContainer>
    );
  }, [altFunction, isNewVehicle, isReservedVehicle, modelImage, vehicleDetailsUrl]);

  const renderStatus = useMemo(() => {
    return (
      <Status
        popoverDescription={popoverDescription}
        vehicleStatus={vehicleStatus}
        statusForDot={statusForDot}
        iconColor={COLORS.grey60}
        oaddStockNumber={stockNumber ?? ''}
      />
    );
  }, [popoverDescription, statusForDot, stockNumber, vehicleStatus]);

  if (
    // If we get null, we hide it
    isAvailableNow === null ||
    // if availableNow is boolean false and
    // vehicleOrderStatus not one of the below three, we hide it
    (!isAvailableNow &&
      orderStatusText &&
      !['Intransit From Factory', 'Port Stock', 'Intransit Inland'].includes(orderStatusText))
    // if availableNow is boolean false and
    // saleOrderType not the one below, we hide it
    // (!isAvailableNow &&
    //   saleOrderType &&
    //   !['SHIP - Deliverable Inventory Order'].includes(saleOrderType))
  )
    return null;

  return (
    <>
      <CardContainer
        isReservedVehicle={isReservedVehicle}
        // href={vehicleDetailsUrl}
        // tabIndex={0}
        // data-cy="vehicle-card-button"
        noPadding
        relative
      >
        {renderGallery}
        <CardContentPadding>
          <VehicleInfo>
            <T3StatusContainer>{renderStatus}</T3StatusContainer>
            <VehicleName data-testid="model-name" data-cy="vehicle-card-model-name">
              {!!model?.salesModelyear} {modelName}
            </VehicleName>
            <VehicleTrim data-testid="trim-name" data-cy="vehicle-card-trim-name">
              {deleteHPfromTrim(subtitleText)}
            </VehicleTrim>
            {!isNewVehicle && isCPO && renderCpoBlock}
            {!isNewVehicle && modelMileage && (
              <Milage data-testid="model-mileage" data-cy="vehicle-card-mileage">
                {t('vlp.vehicleResults.vehicleCard.mileageTier3', {
                  modelMileage,
                  unitOfMeasurement,
                })}
              </Milage>
            )}
            {isMultipleDealers && (
              <Location
                dealerName={dealer?.name}
                unitOfMeasurement={unitOfMeasurement}
                distanceFromUser={geoDistance?.value?.number}
                isNewVehicle={isNewVehicle}
                noFixHeight
              />
            )}
          </VehicleInfo>
          <PriceBreakdown
            typedPrices={carPrices ?? []}
            onModalOpen={onModalOpen}
            isNewVehicle={isNewVehicle}
            rebate={rebate}
          />
          <VdpCtaContainer>
            <VdpCTA href={vehicleDetailsUrl}>{t('vlp.vehicleResults.vehicleCard.ctas.vdp')}</VdpCTA>
          </VdpCtaContainer>
        </CardContentPadding>
      </CardContainer>
      {vendorCtaGroup?.length > 0 && (
        <VendorCTAsGroup
          ctas={vendorCtaGroup}
          vin={vin}
          vehicleId={id}
          market={marketType}
          mainDealer={mainDealer}
          vehicleType={vehicleDataType}
          vehicleData={stockCar}
        />
      )}
      <Modal isOpen={modalState?.isOpen} onClose={onModalClose} data-cy="vehicle-card-legal-modal">
        <Legal>
          <DisclaimerTitleWrapper>
            <HeadlineOrderTwo color={COLORS.black}>{modalState?.title}</HeadlineOrderTwo>
          </DisclaimerTitleWrapper>
          <TextContainer>{modalState?.text}</TextContainer>
        </Legal>
      </Modal>
    </>
  );
};

export default T3VehicleCard;
